/**
 * Admitad Affiliate Tracking
 *
 * Handles cookie-based affiliate attribution for Admitad network.
 * - Reads utm_source from URL to identify traffic source
 * - Sets deduplication cookies for admitad, google, facebook, cj
 * - Stores admitad_uid in cookies for order commission tracking
 * - Injects admitad_uid into cart notes for backend attribution
 */

const COOKIE_NAME = 'deduplication_cookie';
const ADMITAD_UID_COOKIE_NAME = 'admitad_uid';
const DAYS_TO_STORE = 30;
const CHANNEL_NAME = 'utm_source';
const DEDUPLICATION_VALUE = 'admitad';

/**
 * Get a URL search parameter by name
 * @param {string} name - parameter name
 * @returns {string} parameter value or empty string
 */
const getUrlParameter = (name) => {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) ?? '';
};

/**
 * Get a cookie value by name
 * @param {string} name - cookie name
 * @returns {string|undefined} cookie value
 */
const getCookie = (name) => {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

/**
 * Set a cookie with expiration
 * @param {string} name - cookie name
 * @param {string} value - cookie value
 * @param {number} days - days until expiration
 */
const setCookie = (name, value, days) => {
  const expires = new Date(Date.now() + days * 86400000);
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}`;
};

/**
 * Determine and set the traffic source deduplication cookie
 */
const setSourceCookie = () => {
  const params = new URLSearchParams(window.location.search);
  let source = params.get(CHANNEL_NAME) ?? '';

  if (!source && params.get('gclid')) {
    source = 'google';
  } else if (!source && params.get('fbclid')) {
    source = 'facebook';
  } else if (!source && params.get('cjevent')) {
    source = 'cj';
  }

  if (!source) return;

  setCookie(COOKIE_NAME, source, DAYS_TO_STORE);
};

/**
 * Store admitad_uid cookie when traffic comes from Admitad
 */
const setAdmitadUidCookie = () => {
  const utmSource = getUrlParameter(CHANNEL_NAME);

  if (utmSource !== DEDUPLICATION_VALUE) return;

  const admitadUid = getUrlParameter(ADMITAD_UID_COOKIE_NAME);

  if (!admitadUid) return;

  setCookie(ADMITAD_UID_COOKIE_NAME, admitadUid, DAYS_TO_STORE);
  window.admitadUid = admitadUid;
};

/**
 * Append admitad_uid to a cart note string
 * @param {string} currentNote - existing cart note
 * @param {string} admitadUid - admitad user ID
 * @returns {string} updated note
 */
const appendAdmitadUidToNote = (currentNote, admitadUid) => {
  const admitadNote = `admitad_uid=${admitadUid}`;

  if (!currentNote || currentNote.trim() === '') {
    return admitadNote;
  }

  if (currentNote.includes('admitad_uid=')) {
    return currentNote.replace(/admitad_uid=[^;\n\r]*/g, admitadNote);
  }

  return `${currentNote}; ${admitadNote}`;
};

/**
 * Update cart note with admitad_uid for order attribution
 */
const updateCartNote = async () => {
  const admitadUid = getCookie(ADMITAD_UID_COOKIE_NAME) ?? window.admitadUid;

  if (!admitadUid) return;

  try {
    const cartResponse = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`);
    const cart = await cartResponse.json();
    const currentNote = cart.note ?? '';
    const newNote = appendAdmitadUidToNote(currentNote, admitadUid);

    if (newNote === currentNote) return;

    await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: newNote }),
      keepalive: true,
    });
  } catch (error) {
    console.warn('Admitad: Failed to update cart note', error);
  }
};

/**
 * Initialize tracking on page load
 */
const initAdmitadTracking = () => {
  setSourceCookie();
  setAdmitadUidCookie();

  const admitadUid = getCookie(ADMITAD_UID_COOKIE_NAME) ?? window.admitadUid;

  if (admitadUid) {
    updateCartNote();
  }
};

document.addEventListener('DOMContentLoaded', initAdmitadTracking);
